import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import type { AdminTrackerFilters } from "@/features/admin/trackers/api/adminTrackersApi";
import { useAdminTrackers } from "@/features/admin/trackers/api/adminTrackersApi";
import { ADMIN_TRACKERS_COLUMNS, AdminTrackersTable } from "@/features/admin/trackers/components/AdminTrackersTable";
import type { AdminTrackersFilterValues } from "@/features/admin/trackers/components/AdminTrackersToolbar";
import {
    emptyAdminTrackersFilterValues,
    AdminTrackersToolbar,
} from "@/features/admin/trackers/components/AdminTrackersToolbar";
import { TRACKER_REMINDER_STATUSES } from "@/features/admin/trackers/reminderStatus";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const optionalSearchBoolean = z.preprocess((val) => {
    if (val === true || val === "true") return true;
    if (val === false || val === "false") return false;
    return undefined;
}, z.boolean().optional());

const TrackersSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    /** Tri-state: omit = all, true/false filter by diary link. */
    hasDiary: optionalSearchBoolean,
    diaryId: z.string().optional(),
    organisationId: z.string().optional(),
    formId: z.string().optional(),
    reminderStatus: z.enum(TRACKER_REMINDER_STATUSES).optional(),
});

export const Route = createFileRoute("/_app/admin/trackers")({
    component: AdminTrackersPage,
    validateSearch: (search) => TrackersSearchZ.parse(search),
});

function AdminTrackersPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Trackers", route: { to: "/admin/trackers" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const [filterValues, setFilterValues] = useState<AdminTrackersFilterValues>(() => ({
        ...emptyAdminTrackersFilterValues,
        hasDiary: search.hasDiary ?? null,
        diaryId: search.diaryId ?? null,
        organisationId: search.organisationId ?? null,
        formId: search.formId ?? null,
        reminderStatus: search.reminderStatus ?? null,
    }));

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_TRACKERS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: 1,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    useEffect(() => {
        setFilterValues((prev) => ({
            ...prev,
            hasDiary: search.hasDiary ?? null,
            diaryId: search.diaryId ?? null,
            organisationId: search.organisationId ?? null,
            formId: search.formId ?? null,
            reminderStatus: search.reminderStatus ?? null,
        }));
    }, [search.hasDiary, search.diaryId, search.organisationId, search.formId, search.reminderStatus]);

    const filters: AdminTrackerFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            hasDiary: filterValues.hasDiary ?? undefined,
            diaryId: filterValues.diaryId ?? undefined,
            organisationId: filterValues.organisationId ?? undefined,
            formId: filterValues.formId ?? undefined,
            reminderStatus: filterValues.reminderStatus ?? undefined,
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminTrackers(filters);
    const trackers = data?.trackers ?? [];

    return (
        <AdminPageLayout
            title="Trackers"
            description="Browse questionnaire trackers. Use hasDiary=false for orphaned trackers without a diary."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs />

                <AdminTrackersToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setFilterValues(values);
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                page: 1,
                                hasDiary: values.hasDiary ?? undefined,
                                diaryId: values.diaryId ?? undefined,
                                organisationId: values.organisationId ?? undefined,
                                formId: values.formId ?? undefined,
                                reminderStatus: values.reminderStatus ?? undefined,
                            }),
                            replace: true,
                        });
                    }}
                    isApplying={isFetching}
                >
                    <AdminColumnsDialog
                        columns={ADMIN_TRACKERS_COLUMNS}
                        visibleKeys={visibleKeys}
                        onSave={setVisibleKeys}
                    />
                </AdminTrackersToolbar>

                {filterValues.reminderStatus ? (
                    <p className="text-muted-foreground text-xs">
                        Filtered to reminder status{" "}
                        <span className="font-medium text-foreground">{filterValues.reminderStatus}</span>
                        {" · "}
                        <button
                            type="button"
                            className="text-sky-700 underline"
                            onClick={() => {
                                const cleared = { ...filterValues, reminderStatus: null };
                                setFilterValues(cleared);
                                void navigate({
                                    search: (prev) => ({ ...prev, reminderStatus: undefined, page: 1 }),
                                    replace: true,
                                });
                            }}
                        >
                            Clear reminder filter
                        </button>
                    </p>
                ) : null}

                {filterValues.hasDiary === false ? (
                    <p className="bg-amber-50 px-3 py-2 border border-amber-200 rounded-md text-amber-800 text-xs">
                        Showing trackers with no diary link (orphaned).
                    </p>
                ) : null}

                {filterValues.diaryId ? (
                    <p className="text-muted-foreground text-xs">
                        Filtered to diary <span className="font-mono">{filterValues.diaryId}</span>
                        {" · "}
                        <button
                            type="button"
                            className="text-sky-700 underline"
                            onClick={() => {
                                const cleared = { ...filterValues, diaryId: null };
                                setFilterValues(cleared);
                                void navigate({
                                    search: (prev) => ({ ...prev, diaryId: undefined, page: 1 }),
                                    replace: true,
                                });
                            }}
                        >
                            Clear diary filter
                        </button>
                    </p>
                ) : null}

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading trackers: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminTrackersTable trackers={trackers} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
